import { useEffect, useRef } from 'react';
import { editor } from 'monaco-editor';
import { useRouter } from 'next/router';
import { TutorialType } from '@customtypes/tutorial';
import { CodingLanguage } from '@customtypes/parsedLesson';
import { trackCodeRun } from '@customtypes/events';
import { useMixpanel } from '@utils/tracking';
import { TrackId } from '@utils/constants';
import dayjs from 'dayjs';

export const useActiveCoderTracking = ({
  editorInstance,
  languages,
  tutorialType: tutorial_type,
}: {
  editorInstance: editor.IStandaloneCodeEditor | null | undefined;
  languages: CodingLanguage[];
  tutorialType?: TutorialType;
}) => {
  const router = useRouter();
  const mixpanel = useMixpanel();
  const pathname = router.pathname;

  const track_id = router.query.trackId
    ? (Number(router.query.trackId) as TrackId)
    : null;
  const source: 'playground' | 'executable_lesson' =
    pathname === '/web/build/[projectId]' ? 'playground' : 'executable_lesson';

  const lastInputDateRef = useRef<Date | null>(null);

  function sendAndReset() {
    if (lastInputDateRef.current) {
      trackCodeRun(mixpanel, {
        source,
        ...(source === 'executable_lesson' ? { tutorial_type } : {}),
        ...(track_id != null ? { track_id } : {}),
        languages,
        coding_time_seconds: dayjs(new Date()).diff(
          lastInputDateRef.current,
          'seconds',
        ),
      });
      lastInputDateRef.current = new Date();
    }
  }

  useEffect(() => {
    if (!editorInstance) return;

    const updateDateOnUserInput = (e: editor.IModelContentChangedEvent) => {
      // Check if the change is due to user input
      if (e.isFlush || lastInputDateRef.current !== null) {
        // If the change is a flush (not user input), do nothing
        return;
      }
      // Set the last input date to now
      lastInputDateRef.current = new Date();
    };

    const disposable = editorInstance.onDidChangeModelContent(
      updateDateOnUserInput,
    );

    return () => {
      disposable.dispose();
    };
  }, [editorInstance]);

  return { send: sendAndReset };
};
